import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  ExitToApp as ExitIcon,
  Input as EntryIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useNotifications, Notification } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification, clearAllNotifications, hasMore, loading, loadMore } = useNotifications();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getNotificationIcon = (notification: Notification) => {
    // Geofence-specific icons
    if (notification.category === 'geofence') {
      const violationType = notification.metadata?.violationType;
      const eventType = notification.metadata?.eventType;

      if (violationType === 'unauthorized_entry') {
        return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      } else if (violationType === 'unauthorized_exit') {
        return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      } else if (violationType === 'speed_violation') {
        return <SpeedIcon sx={{ color: '#ed6c02' }} />;
      } else if (eventType === 'entry') {
        return <EntryIcon sx={{ color: '#2e7d32' }} />;
      } else if (eventType === 'exit') {
        return <ExitIcon sx={{ color: '#ed6c02' }} />;
      }
      return <ShieldIcon sx={{ color: '#1976d2' }} />;
    }

    // Default icons
    switch (notification.type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: Notification['category']) => {
    switch (category) {
      case 'geofence':
        return '#9c27b0'; // Purple for geofence
      case 'safety':
        return '#d32f2f'; // Red for safety
      case 'device':
        return '#1976d2'; // Blue for device
      case 'maintenance':
        return '#ed6c02'; // Orange for maintenance
      default:
        return '#757575'; // Gray for system
    }
  };

  const getCategoryLabel = (category: Notification['category']) => {
    const labels = {
      geofence: '🛡️ Geofence',
      safety: '⚠️ Safety',
      device: '📡 Device',
      maintenance: '🔧 Maintenance',
      fuel: '⛽ Fuel',
      system: '💻 System'
    };
    return labels[category] || category;
  };

  const open = Boolean(anchorEl);

  // Sort notifications: unread first, then by timestamp
  const sortedNotifications = [...notifications].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{ position: 'relative' }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? (
              <NotificationsActiveIcon sx={{ animation: unreadCount > 0 ? 'ring 2s ease-in-out infinite' : 'none' }} />
            ) : (
              <NotificationsIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { 
            width: 450, 
            maxHeight: 650,
            '@keyframes ring': {
              '0%, 100%': { transform: 'rotate(0deg)' },
              '10%, 30%': { transform: 'rotate(-10deg)' },
              '20%, 40%': { transform: 'rotate(10deg)' },
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Notifications
              {unreadCount > 0 && (
                <Chip 
                  label={unreadCount} 
                  size="small" 
                  color="error" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
            <Stack direction="row" spacing={1}>
              {unreadCount > 0 && (
                <Button size="small" onClick={markAllAsRead}>
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button size="small" color="error" onClick={clearAllNotifications}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Box>

          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 500, overflow: 'auto', p: 0 }}>
              {sortedNotifications.slice(0, 20).map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      bgcolor: notification.read ? 'inherit' : 'action.hover',
                      borderRadius: 1,
                      mb: 1,
                      cursor: 'pointer',
                      border: notification.priority === 'critical' ? '2px solid #d32f2f' : 'none',
                      '&:hover': {
                        bgcolor: notification.read ? 'action.hover' : 'action.selected',
                      },
                      position: 'relative',
                      pl: 1.5
                    }}
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    {/* Priority indicator stripe */}
                    {!notification.read && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          bgcolor: notification.priority === 'critical' ? '#d32f2f' : '#1976d2',
                          borderRadius: '4px 0 0 4px'
                        }}
                      />
                    )}

                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification)}
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: notification.read ? 'normal' : 'bold',
                              flex: 1
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={notification.priority}
                            color={getPriorityColor(notification.priority) as any}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {notification.message}
                          </Typography>

                          {/* Geofence metadata display */}
                          {notification.category === 'geofence' && notification.metadata && (
                            <Box sx={{ mb: 1 }}>
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                {notification.metadata.plateNumber && (
                                  <Chip
                                    size="small"
                                    label={`🚗 ${notification.metadata.plateNumber}`}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                                {notification.metadata.zoneName && (
                                  <Chip
                                    size="small"
                                    label={`📍 ${notification.metadata.zoneName}`}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                                {notification.metadata.currentSpeed && (
                                  <Chip
                                    size="small"
                                    label={`⚡ ${Math.round(notification.metadata.currentSpeed)} km/h`}
                                    variant="outlined"
                                    color="error"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={getCategoryLabel(notification.category)}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: getCategoryColor(notification.category),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                              </Typography>
                            </Stack>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeNotification(notification.id);
                              }}
                              sx={{ ml: 1 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < sortedNotifications.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* ✅ LOAD MORE BUTTON */}
{hasMore && (
  <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
    <Button 
      onClick={loadMore} 
      disabled={loading}
      variant="outlined"
      size="small"
      fullWidth
    >
      {loading ? 'Loading...' : 'Load More'}
    </Button>
  </Box>
)}

{!hasMore && notifications.length > 10 && (
  <Box sx={{ textAlign: 'center', mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
    <Typography variant="caption" color="text.secondary">
      All notifications loaded ({notifications.length} total)
    </Typography>
  </Box>
)}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;